import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { GenesysEmailOutgoingHeader } from './models/genesys-email-outgoing-header.model';
import * as emailAddressHelper from 'email-addresses';
import { cloneDeep, isEmpty } from 'lodash';
import { EmailAddressType } from './models/email-address-type.enum';
import { EmailAddressState, EmailAddressStates } from './models/email-address-state.model';
import { isEmail } from 'validator';
import { getFormattedAddress, getFormattedAddresses } from 'common/emails/email-address-helper';

@Component({
  selector: 'app-genesys-email-outgoing-header',
  templateUrl: './genesys-email-outgoing-header.component.html',
  styleUrls: ['./genesys-email-outgoing-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysEmailOutgoingHeaderComponent implements OnInit {

  @Input() model: GenesysEmailOutgoingHeader;

  @Input() displayActions = true;

  @Output()
  save = new EventEmitter<GenesysEmailOutgoingHeader>();

  @Output()
  cancel = new EventEmitter<GenesysEmailOutgoingHeader>();

  @Output()
  validate = new EventEmitter<boolean>();

  @Output()
  headerModelChange = new EventEmitter<Partial<GenesysEmailOutgoingHeader>>();

  private originalModel: GenesysEmailOutgoingHeader;

  readonly addressStates = new EmailAddressStates();
  readonly emailAddressTypes = EmailAddressType;

  from: string;
  to: string;
  cc: string;
  bcc: string;

  ngOnInit() {
    this.originalModel = cloneDeep(this.model);

    this.from = getFormattedAddress(this.model.from);
    this.to = getFormattedAddresses(this.model.to);
    this.cc = getFormattedAddresses(this.model.cc);
    this.bcc = getFormattedAddresses(this.model.bcc);

    this._validateEmail(this.to, EmailAddressType.TO);
    this._validateEmail(this.cc, EmailAddressType.CC);
    this._validateEmail(this.bcc, EmailAddressType.BCC);
  }

  validateEmailAndEmitModel(value: string, addressType: EmailAddressType): void {
    this._validateEmail(value, addressType);

    this.headerChange({ [addressType]: [{ email: value }] });
  }

  headerChange(change: Partial<GenesysEmailOutgoingHeader>): void {
    const isValid = this.isHeaderStateValid();
    if (isValid) {
      this.headerModelChange.emit(change);
    }
  }

  private _validateEmail(value: string, addressType: EmailAddressType): void {
    const state = <EmailAddressState>this.addressStates[addressType];

    const segments = value.split(';');

    const emails = segments.map((s) => ({
      raw: s,
      parsed: emailAddressHelper.parseOneAddress(s) as emailAddressHelper.ParsedMailbox
    }));

    const invalidEmails = emails.filter((email) => {
      return !email.parsed || !isEmail(email.parsed.parts.address.tokens.trim(), { require_tld: true });
    });

    const validEmails = emails.filter((email) => invalidEmails.indexOf(email) === -1);

    this.model[addressType] = validEmails.map((email) => {
      const emailAddress = { name: email.parsed.name, email: email.parsed.address };
      return emailAddress;
    });

    if (invalidEmails.some((e) => e.raw)) {
      state.InvalidAddresses = invalidEmails
        .map((e) => e.raw)
        .join('; ')
        .trim();
      state.IsValid = false;
    } else {
      state.InvalidAddresses = '';
      state.IsValid = true;
    }

    this.validate.emit(this.isHeaderStateValid());
  }

  isToEmpty(): boolean {
    return isEmpty(this.model.to);
  }

  isValid(addressType: EmailAddressType): boolean {
    return (<EmailAddressState>this.addressStates[addressType]).IsValid;
  }

  isHeaderStateValid(): boolean {
    return (
      !this.isToEmpty() &&
      this.isValid(EmailAddressType.TO) &&
      this.isValid(EmailAddressType.CC) &&
      this.isValid(EmailAddressType.BCC)
    );
  }

  savePressed(): void {
    this.save.emit(this.model);
  }

  cancelPressed(): void {
    this.model = cloneDeep(this.originalModel);
    this.cancel.emit(this.model);
  }
}
